import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, useMapEvents, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { debounce } from 'lodash';

import { useSettings } from '../context/SettingsContext';
import useWebSocket from '../hooks/useWebSocket';
import config from '../config.json';

import GymMarker from './GymMarker';
import GymPopup from './GymPopup';
import SettingsMenu from './SettingsMenu';
import { calculateCanvasBoundaries } from '../utils/canvas';
import { logInfo, logError } from '../utils/logger';
import websocketService from '../services/websocket';

// Create a separate component for handling map events
const MapEvents = ({ onBoundsChange }) => {
  const map = useMapEvents({
    moveend: () => {
      onBoundsChange(map.getBounds());
    },
    zoomend: () => {
      onBoundsChange(map.getBounds());
    }
  });
  
  return null;
};

let mapGlobal, mapInitialized = false;
const MapInitializer = ({ onStartup }) => {
  const map = useMap();
  
  if (mapInitialized) return null;
  
  mapGlobal = map;
  
  const savedMapState = localStorage.getItem('mapState');

  logInfo('savedMapState', savedMapState);
  if (savedMapState) {
    const { center, zoom } = JSON.parse(savedMapState);
	if (map.getCenter() != center && map.getZoom() != zoom) {
      map.setView(center, zoom);
      logInfo('Map setView', {
        center: center,
        zoom: zoom
      });
	}
  }
  
  logInfo('Map initialized', {
    center: map.getCenter(),
    zoom: map.getZoom()
  });
  
  // Get initial data
  onStartup(map.getBounds());
  mapInitialized = true;
  
  return null;
};

const GymMap = () => {
  const { mapTileProvider } = useSettings();
  const {
    connected,
    gyms,
    selectedGym,
    gymMonsters,
    gymEvents,
    updateCanvas,
    getGymDetails,
    updateBadgeLevel
  } = useWebSocket();
  
  const [showSettings, setShowSettings] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [center] = useState({
    lat: config.map_start_location.latitude,
    lng: config.map_start_location.longitude
  });
  
  const mapRef = useRef(null);
  
  const [mapCenter, setMapCenter] = useState(() => {
    const savedCenter = localStorage.getItem("mapCenter");
    return savedCenter ? JSON.parse(savedCenter) : [config.map_start_location.latitude, config.map_start_location.longitude];
  });

  const [mapZoom, setMapZoom] = useState(() => {
    const savedZoom = localStorage.getItem("mapZoom");
    return savedZoom ? JSON.parse(savedZoom) : config.map_start_zoom;
  });
  
  // Debounce the boundary updates to prevent too many requests
  const debouncedUpdateCanvas = useCallback(
    debounce((bounds) => {
      const map = mapGlobal;
      //logInfo('bounds', bounds, map);
      updateCanvas(bounds);
      
      // Save current map state to localStorage
      if (map) {
        const center = map.getCenter();
        const zoom = map.getZoom();
        localStorage.setItem('mapState', JSON.stringify({
          center: [center.lat, center.lng],
          zoom: zoom
        }));
      }
    }, 2000),
    [updateCanvas]
  );
  
  const handleMarkerClick = (gym) => {
    getGymDetails(gym.id);
    setIsPopupOpen(true);
  };
  
  const handlePopupClose = () => {
    setIsPopupOpen(false);
  };
  
  const handleBadgeUpdate = (gymId, badgeLevel) => {
    updateBadgeLevel(gymId, badgeLevel);
  };
  
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  
  return (
    <div className="gym-map-container">
      <div className={`settings-button ${showSettings ? 'active' : ''}`} onClick={toggleSettings}>
        <i className="fa fa-cog"></i>
      </div>
      
      {showSettings && <SettingsMenu onClose={toggleSettings} />}
      
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        minZoom={config.map_min_zoom}
        maxZoom={config.map_max_zoom}
        zoomControl={false}
        className="map-container"
      >
        <TileLayer
          attribution=''
          url={mapTileProvider.url}
        />
        
        <ZoomControl position="bottomright" />
        
		<MapInitializer onStartup={updateCanvas} />
		<MapEvents onBoundsChange={debouncedUpdateCanvas} />
        
        {gyms.map((gym) => (
          <GymMarker
            key={gym.id}
            gym={gym}
            onClick={() => handleMarkerClick(gym)}
          />
        ))}
      </MapContainer>
      
      {selectedGym && isPopupOpen && (
        <GymPopup
          gym={selectedGym}
          monsters={gymMonsters}
          events={gymEvents}
          onClose={handlePopupClose}
          onBadgeUpdate={handleBadgeUpdate}
          onTimeSpanChange={(timeSpan) => getGymDetails(selectedGym.id, timeSpan)}
        />
      )}
      
      <div className={`connection-status ${websocketService.isConnected ? 'connected' : 'disconnected'}`}>
        {websocketService.isConnected ? 'Connected' : 'Disconnected'}
      </div>
    </div>
  );
};

export default GymMap;